import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { SupplierModel, PaymentMethodEnum } from '../models/SupplierModel';
import { Modal } from 'react-bootstrap';

type Props = {
  supplier?: SupplierModel;
  visible: boolean;
  onSaveSupplier: (supplier: SupplierModel) => void;
  onCancel: () => void;
};

const EMPTY_SUPPLIER: SupplierModel = {
  id: 0,
  name: '',
  vat_number: '',
  email: '',
  phone: '',
  state: 'active',
  payment_method: PaymentMethodEnum.WIRE_TRANSFER,
  max_payment_days: 30,
  total_discounts: 0,
  discount_on: 'order',
  discount_mode: 'absolute',
};

export default function ModalSupplierForm({
  supplier,
  visible,
  onSaveSupplier,
  onCancel,
}: Props) {
  const intl = useIntl();
  const [supplierState, setSupplierState] = useState(supplier);

  useEffect(() => {
    if (supplier) {
      setSupplierState(supplier);
    } else {
      setSupplierState(EMPTY_SUPPLIER);
    }
  }, [supplier, visible]);

  return (
    <Modal show={visible} onClose={onCancel}>
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">
            {supplierState?.id
              ? intl.formatMessage({ id: 'suppliers.editSupplier' })
              : intl.formatMessage({ id: 'suppliers.addSupplier' })}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={onCancel}
          ></button>
        </div>
        <div className="modal-body">
          <form>
            <div className="row mb-3">
              <div className="col-6">
                <label htmlFor="name" className="form-label">
                  {intl.formatMessage({ id: 'suppliers.name' })} *
                </label>
                <input
                  type="text"
                  value={supplierState?.name}
                  onChange={(e) =>
                    setSupplierState({
                      ...supplierState,
                      name: e.target.value,
                    })
                  }
                  className="form-control"
                  id="name"
                  placeholder={intl.formatMessage({
                    id: 'suppliers.namePlaceholder',
                  })}
                />
              </div>
              <div className="col-6">
                <label htmlFor="vat_number" className="form-label">
                  {intl.formatMessage({ id: 'suppliers.vatNumber' })} *
                </label>
                <input
                  type="text"
                  value={supplierState?.vat_number}
                  onChange={(e) =>
                    setSupplierState({
                      ...supplierState,
                      vat_number: e.target.value,
                    })
                  }
                  className="form-control"
                  id="vat_number"
                  placeholder={intl.formatMessage({
                    id: 'suppliers.vatNumberPlaceholder',
                  })}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-6">
                <label htmlFor="email" className="form-label">
                  {intl.formatMessage({ id: 'suppliers.email' })}
                </label>
                <input
                  type="email"
                  required
                  value={supplierState?.email}
                  onChange={(e) =>
                    setSupplierState({
                      ...supplierState,
                      email: e.target.value,
                    })
                  }
                  className="form-control"
                  id="email"
                  placeholder={intl.formatMessage({
                    id: 'suppliers.emailPlaceholder',
                  })}
                />
              </div>
              <div className="col-6">
                <label htmlFor="phone" className="form-label">
                  {intl.formatMessage({ id: 'suppliers.phone' })}
                </label>
                <input
                  type="text"
                  value={supplierState?.phone}
                  onChange={(e) =>
                    setSupplierState({
                      ...supplierState,
                      phone: e.target.value,
                    })
                  }
                  className="form-control"
                  id="phone"
                  maxLength={14}
                  placeholder={intl.formatMessage({
                    id: 'suppliers.phonePlaceholder',
                  })}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-6">
                <label htmlFor="payment_method" className="form-label">
                  {intl.formatMessage({ id: 'suppliers.paymentMethod' })}
                </label>
                <select
                  className="form-select"
                  value={supplierState?.payment_method}
                  onChange={(e) =>
                    setSupplierState({
                      ...supplierState,
                      payment_method: e.target.value as PaymentMethodEnum,
                    })
                  }
                  id="payment_method"
                >
                  <option value={PaymentMethodEnum.BANK_DRAFT}>
                    {intl.formatMessage({ id: 'suppliers.bankDraft' })}
                  </option>
                  <option value={PaymentMethodEnum.WIRE_TRANSFER}>
                    {intl.formatMessage({ id: 'suppliers.wireTransfer' })}
                  </option>
                </select>
              </div>
              <div className="col-6">
                <label htmlFor="max_payment_days" className="form-label">
                  {intl.formatMessage({ id: 'suppliers.maxPaymentDays' })}
                </label>
                <input
                  type="number"
                  value={supplierState?.max_payment_days}
                  onChange={(e) =>
                    setSupplierState({
                      ...supplierState,
                      max_payment_days: parseInt(e.target.value),
                    })
                  }
                  className="form-control"
                  id="max_payment_days"
                  placeholder={intl.formatMessage({
                    id: 'suppliers.maxPaymentDaysPlaceholder',
                  })}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-6">
                <label htmlFor="discount_mode" className="form-label">
                  {intl.formatMessage({ id: 'suppliers.discountMode' })}
                </label>
                <select
                  className="form-select"
                  value={supplierState?.discount_mode}
                  onChange={(e) =>
                    setSupplierState({
                      ...supplierState,
                      discount_mode: e.target.value as 'absolute' | 'relative',
                    })
                  }
                  id="discount_mode"
                >
                  <option value="absolute">
                    {intl.formatMessage({ id: 'suppliers.absolute' })}
                  </option>
                  <option value="relative">
                    {intl.formatMessage({ id: 'suppliers.relative' })}
                  </option>
                </select>
              </div>
              <div className="col-6">
                <label htmlFor="total_discounts" className="form-label">
                  {intl.formatMessage({ id: 'suppliers.totalDiscounts' })}
                </label>
                <input
                  type="number"
                  value={supplierState?.total_discounts}
                  onChange={(e) =>
                    setSupplierState({
                      ...supplierState,
                      total_discounts: parseInt(e.target.value),
                    })
                  }
                  className="form-control"
                  id="total_discounts"
                  placeholder={intl.formatMessage({
                    id: 'suppliers.totalDiscountsPlaceholder',
                  })}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-6">
                <label htmlFor="discount_on" className="form-label">
                  {intl.formatMessage({ id: 'suppliers.discountOn' })}
                </label>
                <select
                  className="form-select"
                  value={supplierState?.discount_on}
                  onChange={(e) =>
                    setSupplierState({
                      ...supplierState,
                      discount_on: e.target.value as 'order' | 'order_line',
                    })
                  }
                  id="discount_on"
                >
                  <option value="order">
                    {intl.formatMessage({ id: 'suppliers.order' })}
                  </option>
                  <option value="order_line">
                    {intl.formatMessage({ id: 'suppliers.orderLine' })}
                  </option>
                </select>
              </div>
              <div className="col-6">
                <label
                  htmlFor="special_payment_due_date"
                  className="form-label"
                >
                  {intl.formatMessage({
                    id: 'suppliers.specialPaymentDueDate',
                  })}
                </label>
                <input
                  type="number"
                  value={supplierState?.special_payment_due_date}
                  onChange={(e) =>
                    setSupplierState({
                      ...supplierState,
                      special_payment_due_date: parseInt(e.target.value),
                    })
                  }
                  className="form-control"
                  id="special_payment_due_date"
                  placeholder={intl.formatMessage({
                    id: 'suppliers.specialPaymentDueDatePlaceholder',
                  })}
                />
              </div>
            </div>
          </form>

          <div className="d-flex gap-4 pt-8 justify-content-end">
            <button
              type="button"
              className="btn btn-tertiary"
              data-bs-dismiss="modal"
              onClick={onCancel}
            >
              {intl.formatMessage({ id: 'common.close' })}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onSaveSupplier(supplierState)}
              disabled={!supplierState?.name || !supplierState?.vat_number}
            >
              <span className="fa fa-save me-3"></span>
              {intl.formatMessage({ id: 'common.save' })}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
